import React, { useContext } from 'react';
import _ from 'lodash';
import { SortableContainer } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import FormContext from '../../FormContext';
import Step from './Step';
import CreateStepPlaceholder from './CreateStepPlaceholder';
import SectionHeader from '../SectionHeader';

const useStyles = makeStyles(theme => ({
  spacer: {
    marginBottom: theme.spacing(2)
  },
  listRoot: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  createPlaceholderContainer: {
    width: '240px',
    height: '450px',
    margin: theme.spacing(2)
  },
  dragging: {
    pointerEvents: 'auto !important',
    cursor: 'grabbing !important'
  }
}));

/**
 * Recalculates the stepNumbers of the steps. The first step gets number 1,
 * second one gets number 2 and so on.
 * @param steps Steps to recalculate step numbers.
 * @returns {*} The array with correct step number sequence
 */
function recalculateStepNumbers(steps) {
  return steps.map((step, index) => {
    return { ...step, stepNumber: index + 1 };
  });
}

const StepInstructionsField = ({
  dataField,
  StepContent,
  header,
  thumbnailHeight,
  thumbnailWidth,
  // Used when a single step includes multiple instructions (each instruction is stored as entry in stepDetails)
  useMultiInstructions = false
}) => {
  const classes = useStyles();
  const { setFieldValue, getFieldValue } = useContext(FormContext);

  const steps = getFieldValue(dataField) || [];
  const sortedSteps = _.sortBy(steps, 'stepNumber');

  function getStep(byStepNumber) {
    const stepIndex = steps.findIndex(step => step.stepNumber === byStepNumber);
    return steps[stepIndex];
  }

  function getStepData(stepNumber) {
    const step = getStep(stepNumber);
    return useMultiInstructions ? step.stepDetails[0] : step;
  }

  function handleChangeImage(stepNumber, image) {
    const stepData = getStepData(stepNumber);
    stepData.image = image;
    setFieldValue(dataField, steps);
  }

  function handleChangeData(stepNumber, field, value) {
    const stepData = getStepData(stepNumber);
    _.set(stepData, field, value);
    setFieldValue(dataField, steps);
  }

  function addStep({ image = null }) {
    const stepNumber = steps.length + 1;
    const key = `id-${Math.round(Math.random() * 1000000)}`;
    const stepData = useMultiInstructions
      ? {
          stepNumber,
          stepDetails: [{ image, order: 0 }],
          key
        }
      : {
          stepNumber,
          image,
          key
        };
    steps.push(stepData);
    setFieldValue(dataField, steps);
  }

  function removeStep(stepNumber) {
    let newSteps = _.filter(
      sortedSteps,
      step => step.stepNumber !== stepNumber
    );
    newSteps = recalculateStepNumbers(newSteps);
    setFieldValue(dataField, newSteps);
  }

  function sortEnd({ oldIndex, newIndex }) {
    let rearrangedSteps = arrayMove(sortedSteps, oldIndex, newIndex);
    rearrangedSteps = recalculateStepNumbers(rearrangedSteps);
    setFieldValue(dataField, rearrangedSteps);
  }

  return (
    <Grid item xs={12}>
      {header && (
        <>
          <SectionHeader translationId={header} />
          <div className={classes.spacer} />
        </>
      )}
      <SortableList
        onSortEnd={sortEnd}
        axis="xy"
        distance={5}
        helperClass={classes.dragging}
        shouldCancelStart={(e) => {
          let el = e.target;

          while (el && el !== e.currentTarget) {
            const nodeName = el.nodeName.toLowerCase();

            // Cancel if the node or one of its parents is an interactive MUI element
            if (
              ['input', 'select', 'textarea', 'button'].includes(nodeName) ||
              el.getAttribute('contenteditable') === 'true' ||
              el.classList.contains('MuiSelect-root') ||
              el.classList.contains('MuiInputBase-root') ||
              el.classList.contains('MuiAutocomplete-root') ||
              el.classList.contains('MuiSlider-root') ||
              el.classList.contains('MuiSwitch-root') ||
              el.classList.contains('MuiCheckbox-root') ||
              el.classList.contains('MuiRadio-root') ||
              el.classList.contains('no-drag') // optional custom escape
            ) {
              return true; // cancel drag
            }

            el = el.parentElement;
          }

          return false; // allow drag
        }}
      >
        <div className={classes.listRoot}>
          {sortedSteps.map((step, index) => {
            const key = `step-${step.id || step.key}`;
            const { stepNumber } = step;
            const stepData = getStepData(stepNumber);
            return (
              <Step
                index={index}
                number={stepNumber}
                image={stepData.image}
                stepData={stepData}
                StepContent={StepContent}
                onChangeImage={image => {
                  handleChangeImage(stepNumber, image);
                }}
                onChangeData={(field, value) => {
                  handleChangeData(stepNumber, field, value);
                }}
                onRemove={() => {
                  removeStep(stepNumber);
                }}
                key={key}
              />
            );
          })}
          <div
            className={classes.createPlaceholderContainer}
            key="form-field-step/create"
          >
            <CreateStepPlaceholder
              thumbnailHeight={thumbnailHeight}
              thumbnailWidth={thumbnailWidth}
              onAddStep={addStep}
            />
          </div>
        </div>
      </SortableList>
    </Grid>
  );
};

const SortableList = SortableContainer(({ children }) => {
  return children;
});

export default StepInstructionsField;
